/**
 * O erro de uma resposta não-ok — sozinho, sem o resto do cliente HTTP.
 *
 * Ele mora num arquivo próprio para que o mapa de mensagens da tela
 * (`preparationCopy`) possa depender dele sem arrastar `api.ts` inteiro: aquele
 * módulo puxa a sessão, o DOM e meio mundo, e um mapa de textos precisa ser
 * conferível sozinho. `api.ts` reexporta os dois nomes, então nada mais no
 * frontend precisou mudar de import.
 */

/**
 * Erro de API com o status HTTP preservado.
 *
 * O status é o que permite à interface reagir ao *tipo* de falha (409 =
 * cache expirado, por exemplo) sem depender do texto da mensagem, que é copy
 * do backend e pode mudar.
 */
export class ApiError extends Error {
  readonly status: number;

  /**
   * O código estruturado do erro, quando o backend manda um.
   *
   * O daemon do render responde `{"detail": {"code": "...", ...}}` — um contrato
   * tipado, estável, feito para ser lido por máquina. Casar por ele é o que
   * permite à tela distinguir «o daemon esqueceu este compare» (que se recria)
   * de «esta comparação não existe» (que não), sem nunca depender do texto da
   * mensagem, que é copy do servidor e muda quando ele muda (F9.4).
   */
  readonly code: string | null;

  /**
   * O `detail` inteiro, quando ele é um objeto tipado.
   *
   * O `code` diz **qual** erro; o resto do objeto diz com que números. A recusa
   * de seleção pequena da F-T.10 manda `min_side_px` junto justamente para a
   * tela não guardar uma cópia do limiar — uma cópia pode divergir do que o
   * servidor aplica, e aí a frase explicaria uma regra que não é a que valeu.
   */
  readonly detail: unknown;

  constructor(
    message: string,
    status: number,
    code: string | null = null,
    detail: unknown = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

/** Monta o erro de uma resposta não-ok, extraindo o `detail` do FastAPI. */
export async function apiErrorFromResponse(res: Response): Promise<ApiError> {
  const text = await res.text();
  let detail: unknown;
  try {
    const body = JSON.parse(text) as Record<string, unknown>;
    detail = body.detail;
  } catch {
    detail = undefined;
  }
  if (typeof detail === "string") {
    return new ApiError(detail, res.status);
  }
  // O erro tipado do daemon chega como objeto. `code` é o que a tela usa para
  // decidir; `message` continua indo para o console, onde ela é útil.
  if (detail !== null && typeof detail === "object") {
    const tipado = detail as { code?: unknown; message?: unknown };
    const code = typeof tipado.code === "string" ? tipado.code : null;
    const message = typeof tipado.message === "string" ? tipado.message : text;
    return new ApiError(message, res.status, code, detail);
  }
  return new ApiError(`${res.status} ${res.statusText}: ${text}`, res.status);
}
